import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { collection, getDocs, deleteDoc, doc, Timestamp, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Match, SerializableMatch, getMatch, convertToSerializableMatch } from '../services/matchService';
import MatchStats from '../components/Match/MatchStats';
import { setCurrentMatch } from '../store/matchSlice';

// Fonction utilitaire pour vérifier si l'utilisateur est connecté
const isUserAuthenticated = () => {
  if (typeof window !== 'undefined') {
    return !!localStorage.getItem('user');
  }
  return false;
};

// Fonction utilitaire pour obtenir les informations de l'utilisateur
const getUserInfo = () => {
  if (typeof window !== 'undefined') {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch (error) {
        console.error('Erreur lors de la lecture des informations utilisateur:', error);
      }
    }
  }
  return null;
};

const StatsPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [matches, setMatches] = useState<Match[]>([]);
  const [selectedMatch, setSelectedMatch] = useState<SerializableMatch | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(isUserAuthenticated());

  const handleMatchSelect = useCallback(async (matchId: string) => {
    try {
      setLoading(true);
      
      // Vérifier si l'utilisateur est authentifié
      if (!isAuthenticated) {
        setError("Vous devez être connecté pour accéder aux statistiques des matchs.");
        setLoading(false);
        return;
      }
      
      // Récupérer les informations de l'utilisateur connecté
      const user = getUserInfo();
      if (!user) {
        setError("Impossible de récupérer les informations utilisateur.");
        setLoading(false);
        return;
      }

      // Récupérer le match spécifié
      const match = await getMatch(matchId);
      if (!match) {
        setError("Match non trouvé.");
        setLoading(false);
        return;
      }

      // Convertir le match en format sérialisable
      const serializableMatch = convertToSerializableMatch(match);
      setSelectedMatch(serializableMatch);
      dispatch(setCurrentMatch(serializableMatch));
      setError(null);
    } catch (error) {
      console.error('[STATS PAGE] Erreur lors de la sélection du match:', error);
      setError("Une erreur s'est produite lors du chargement du match.");
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, dispatch]);

  const loadMatches = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const isDev = process.env.NODE_ENV !== 'production';

      // Vérifier l'authentification avant de charger les matchs
      const isAuth = isUserAuthenticated();
      setIsAuthenticated(isAuth);
      if (!isAuth) {
        setError("Vous devez être connecté pour accéder aux statistiques des matchs.");
        setLoading(false);
        return;
      }

      try {
        // Récupérer les informations de l'utilisateur connecté
        const userStr = localStorage.getItem('user');
        let userId = null;
        let userRole = null;
        
        if (userStr) {
          const user = JSON.parse(userStr);
          // Récupérer l'identifiant utilisateur (email ou id)
          userId = user.email || user.id || null;
          userRole = user.role; // Récupérer le rôle de l'utilisateur
          if (isDev) {
            console.log('[STATS PAGE] Utilisateur authentifié:', { userId, userRole });
          }
        } else {
          setError("Vous devez être connecté pour accéder aux statistiques des matchs.");
          setLoading(false);
          return;
        }
        
        const matchesCollection = collection(db, 'matches');
        const matchesQuery = userRole === 'admin'
          ? query(matchesCollection)
          : query(matchesCollection, where('userId', '==', userId));
        const matchesSnapshot = await getDocs(matchesQuery);
        const matchesList: Match[] = [];

        matchesSnapshot.forEach((doc) => {
          const data = doc.data();
          const matchUserId = data.userId || null;
          
          // Filtrage strict : l'utilisateur ne voit que ses propres matchs
          // Seul l'admin peut voir tous les matchs
          const isAdmin = userRole === 'admin';
          const isOwnMatch = matchUserId === userId;
          
          if (isAdmin || isOwnMatch) {
            const match: Match = {
              id: doc.id,
              name: data.name || '',
              teamA: data.teamA || { id: '', name: '', players: [] },
              teamB: data.teamB || { id: '', name: '', players: [] },
              scoreA: data.scoreA || 0,
              scoreB: data.scoreB || 0,
              date: data.date,
              startTime: data.startTime,
              endTime: data.endTime,
              events: data.events || [],
              status: data.status || 'upcoming',
              time: data.time || '',
              userId: data.userId || null,
            };
            
            matchesList.push(match);
          }
        });
        
        // Trier les matchs par date (du plus récent au plus ancien)
        const sortedMatches = matchesList.sort((a, b) => {
          const dateA = a.date instanceof Timestamp ? a.date.toMillis() : new Date(a.date).getTime();
          const dateB = b.date instanceof Timestamp ? b.date.toMillis() : new Date(b.date).getTime();
          return dateB - dateA;
        });
        
        setMatches(sortedMatches);
        
        // Si aucun match n'est trouvé, afficher un message
        if (sortedMatches.length === 0 && isDev) {
          console.log('[STATS PAGE] Aucun match trouvé pour cet utilisateur');
        }
        
      } catch (error) {
        console.error('[STATS PAGE] Erreur lors du chargement des matchs:', error);
        setError("Une erreur s'est produite lors du chargement des matchs. Veuillez réessayer plus tard.");
      } finally {
        setLoading(false);
      }
    } catch (error) {
      console.error('[STATS PAGE] Erreur:', error);
      setError("Une erreur s'est produite. Veuillez réessayer plus tard.");
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Charger les matchs au chargement de la page
    loadMatches();
    
    // Vérifier s'il y a un ID de match dans l'URL
    const matchId = searchParams.get('id');
    if (matchId) {
      handleMatchSelect(matchId);
    }
    
    // Ajouter un écouteur d'événements pour détecter les changements dans localStorage
    const handleStorageChange = () => {
      setIsAuthenticated(isUserAuthenticated());
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [searchParams, handleMatchSelect, loadMatches]);

  const handleDeleteMatch = async () => {
    if (!selectedMatch) return;
    
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer le match ${selectedMatch.teamA.name} vs ${selectedMatch.teamB.name} ?`)) {
      try {
        setLoading(true);
        
        // Supprimer le match de Firestore
        // Vérifier que l'ID du match existe avant de le supprimer
        if (selectedMatch.id) {
          await deleteDoc(doc(db, 'matches', selectedMatch.id));
        }
        
        // Mettre à jour la liste des matchs
        setMatches(matches.filter(match => match.id !== selectedMatch.id));
        
        // Réinitialiser le match sélectionné
        setSelectedMatch(null);
        
        // Afficher un message de succès
        alert('Le match a été supprimé avec succès.');
        
      } catch (error) {
        console.error('[STATS PAGE] Erreur lors de la suppression du match:', error);
        alert("Une erreur s'est produite lors de la suppression du match. Veuillez réessayer plus tard.");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleGoToLiveMatch = () => {
    if (selectedMatch) {
      navigate(`/live-match/${selectedMatch.id}`);
    }
  };

  const handleEditTeams = () => {
    if (selectedMatch) {
      // Rediriger vers la page de configuration des équipes avec l'ID du match en paramètre
      navigate(`/config-equipe?matchId=${selectedMatch.id}`);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  // Si l'utilisateur n'est pas authentifié, afficher un message
  if (!isAuthenticated) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-6" role="alert">
          <p className="font-bold">Authentification requise</p>
          <p>Vous devez être connecté pour accéder aux statistiques des matchs.</p>
        </div>
        <button 
          onClick={() => navigate('/')} 
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
        >
          Retour à l'accueil
        </button>
      </div>
    );
  }
  
  // Si une erreur s'est produite, afficher un message d'erreur
  if (error) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6" role="alert">
          <p className="font-bold">Erreur</p>
          <p>{error}</p>
        </div>
        <button 
          onClick={() => navigate('/')} 
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline mr-2"
        >
          Retour à l'accueil
        </button>
        <button 
          onClick={() => navigate('/create-match')} 
          className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
        >
          Créer un match
        </button>
      </div>
    );
  }

  return (
    <div className="md:p-4 p-1 bg-white md:shadow-lg md:rounded-lg">
      {/* Sélecteur de match - Version mobile */}
      <div className="md:hidden mb-2">
        <select
          className="w-full p-2 border border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 mb-2"
          value={selectedMatch?.id || ''}
          onChange={(e) => {
            const match = matches.find(m => m.id === e.target.value);
            if (match) {
              setSelectedMatch(convertToSerializableMatch(match));
            } else {
              setSelectedMatch(null);
            }
          }}
        >
          <option value="">Sélectionner un match</option>
          {matches.map((match) => (
            <option key={match.id} value={match.id}>
              {match.teamA.name} vs {match.teamB.name} - {
                new Date(match.date.toMillis()).toLocaleDateString('fr-FR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric'
                })
              }
            </option>
          ))}
        </select>
        <div className="flex space-x-1">
          {selectedMatch && (
            <button
              onClick={handleEditTeams}
              className="flex-1 bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 transition-all duration-300 flex items-center justify-center text-sm"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Éditer
            </button>
          )}
          {selectedMatch && (
            <button
              onClick={handleGoToLiveMatch}
              className="flex-1 bg-green-600 text-white p-2 rounded-lg hover:bg-green-700 transition-all duration-300 flex items-center justify-center text-sm"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Live
            </button>
          )}
          {selectedMatch && (
            <button
              onClick={handleDeleteMatch}
              className="flex-1 bg-red-600 text-white p-2 rounded-lg hover:bg-red-700 transition-all duration-300 flex items-center justify-center text-sm"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Suppr
            </button>
          )}
        </div>
      </div>

      {/* Sélecteur de match - Version desktop */}
      <div className="hidden md:flex mb-6 items-center space-x-4">
        <select
          className="flex-grow p-3 border border-gray-300 rounded-lg shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
          value={selectedMatch?.id || ''}
          onChange={(e) => {
            const match = matches.find(m => m.id === e.target.value);
            if (match) {
              setSelectedMatch(convertToSerializableMatch(match));
            } else {
              setSelectedMatch(null);
            }
          }}
        >
          <option value="">Sélectionner un match</option>
          {matches.map((match) => (
            <option key={match.id} value={match.id}>
              {match.teamA.name} vs {match.teamB.name} - {
                new Date(match.date.toMillis()).toLocaleDateString('fr-FR', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric'
                })
              }
            </option>
          ))}
        </select>

        {selectedMatch && (
          <button
            onClick={handleEditTeams}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-all duration-300 flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Éditer
          </button>
        )}
        {selectedMatch && (
          <button
            onClick={handleGoToLiveMatch}
            className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-all duration-300 flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Live
          </button>
        )}
        {selectedMatch && (
          <button
            onClick={handleDeleteMatch}
            className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-all duration-300 flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Suppr
          </button>
        )}
      </div>

      {selectedMatch && (
        <div className="mt-2 md:mt-8">
          <MatchStats match={selectedMatch} />
        </div>
      )} 
      {!selectedMatch && (
        <div className="text-center text-gray-500 py-4 md:py-8">
          Sélectionnez un match pour voir ses statistiques
        </div>
      )}
    </div>
  );
};

export default StatsPage;
